import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Typography,
  Chip,
  Stack,
  Divider,
  Alert,
  Card,
  CardContent,
  Avatar,
  Grid,
  CircularProgress,
  InputAdornment,
  Tooltip,
  Paper,
} from '@mui/material';
import {
  Add,
  Delete,
  Person,
  Email,
  Phone,
  Business,
  People,
  Close,
  Save,
  Cancel,
} from '@mui/icons-material';
import { getClientContacts, addContactToClient, removeContactFromClient } from '../Services/clientContactService';
import { useToast } from '../context/ToastContext';

export default function ClientContactsManager({ 
  open, 
  onClose, 
  client, 
  onContactsChange 
}) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [newContact, setNewContact] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    password: ''
  });
  const { showToast } = useToast();

  useEffect(() => {
    if (open && client) {
      loadContacts();
    }
  }, [open, client]);

  const loadContacts = async () => {
    try {
      setLoading(true);
      const data = await getClientContacts(client.id);
      setContacts(data.contacts || []);
    } catch (error) {
      showToast('Erreur lors du chargement des contacts', { severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddContact = async () => {
    if (!newContact.nom || !newContact.prenom || !newContact.email) {
      showToast('Nom, prénom et email sont requis', { severity: 'warning' });
      return;
    }

    try {
      setSaving(true);
      await addContactToClient(client.id, newContact);
      showToast('Contact ajouté avec succès', { severity: 'success' });
      setAddContactOpen(false);
      setNewContact({ nom: '', prenom: '', email: '', telephone: '', password: '' });
      loadContacts();
      onContactsChange?.();
    } catch (error) {
      showToast('Erreur lors de l\'ajout du contact', { severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveContact = async (contactId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce contact ?')) {
      return;
    }

    try {
      await removeContactFromClient(client.id, contactId);
      showToast('Contact supprimé avec succès', { severity: 'success' });
      loadContacts();
      onContactsChange?.();
    } catch (error) {
      showToast('Erreur lors de la suppression du contact', { severity: 'error' });
    }
  };

  const getInitials = (prenom, nom) => {
    const first = prenom?.charAt(0)?.toUpperCase() || '';
    const last = nom?.charAt(0)?.toUpperCase() || '';
    return first + last || '?';
  };

  const handleCloseAddDialog = () => {
    setAddContactOpen(false);
    setNewContact({ nom: '', prenom: '', email: '', telephone: '', password: '' });
  };

  if (!client) return null;

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                <People />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="600">
                  Contacts de {client.nom}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                  <Business fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {client.email}
                  </Typography>
                </Stack>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip 
                label={`${contacts.length} contact${contacts.length > 1 ? 's' : ''}`} 
                color="primary" 
                sx={{ fontWeight: 'bold' }}
              />
              <IconButton onClick={onClose} size="small">
                <Close />
              </IconButton>
            </Stack>
          </Stack>
        </DialogTitle>
        
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Section d'ajout */}
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                bgcolor: 'background.default',
                borderStyle: 'dashed',
                borderColor: 'primary.main',
                borderWidth: 2,
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                    Ajouter un nouveau contact
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Créez un nouveau contact pour ce client
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setAddContactOpen(true)}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 4,
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  Nouveau contact
                </Button>
              </Stack>
            </Paper>

            <Divider />

            {/* Liste des contacts */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : contacts.length === 0 ? (
              <Alert 
                severity="info" 
                icon={<People />}
                sx={{ 
                  borderRadius: 2,
                  '& .MuiAlert-message': {
                    width: '100%',
                  }
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Aucun contact pour ce client
                </Typography>
                <Typography variant="body2">
                  Cliquez sur "Nouveau contact" pour ajouter le premier contact.
                </Typography>
              </Alert>
            ) : (
              <Grid container spacing={2}>
                {contacts.map((contact) => (
                  <Grid item xs={12} sm={6} key={contact.id}>
                    <Card
                      sx={{
                        height: '100%',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 4,
                        },
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <CardContent>
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                          <Avatar
                            sx={{
                              bgcolor: 'primary.main',
                              width: 56,
                              height: 56,
                              fontSize: '1.25rem',
                              fontWeight: 'bold',
                            }}
                          >
                            {getInitials(contact.prenom, contact.nom)}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="h6" fontWeight="600" noWrap>
                              {contact.prenom} {contact.nom}
                            </Typography>
                            <Stack spacing={1} mt={1}>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Email fontSize="small" color="action" />
                                <Typography variant="body2" color="text.secondary" noWrap>
                                  {contact.email}
                                </Typography>
                              </Stack>
                              {contact.telephone && (
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Phone fontSize="small" color="action" />
                                  <Typography variant="body2" color="text.secondary">
                                    {contact.telephone}
                                  </Typography>
                                </Stack>
                              )}
                            </Stack>
                          </Box>
                          <Tooltip title="Supprimer le contact">
                            <IconButton
                              color="error"
                              onClick={() => handleRemoveContact(contact.id)}
                              size="small"
                              sx={{
                                '&:hover': {
                                  bgcolor: 'error.light',
                                  color: 'error.contrastText',
                                },
                              }}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Stack>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={onClose}
            variant="outlined"
            startIcon={<Close />}
          >
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal d'ajout de contact */}
      <Dialog 
        open={addContactOpen} 
        onClose={handleCloseAddDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              <Person />
            </Avatar>
            <Typography variant="h6" fontWeight="600">
              Nouveau contact
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Prénom"
                  value={newContact.prenom}
                  onChange={(e) => setNewContact({ ...newContact, prenom: e.target.value })}
                  fullWidth
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Nom"
                  value={newContact.nom}
                  onChange={(e) => setNewContact({ ...newContact, nom: e.target.value })}
                  fullWidth
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Email"
                  type="email"
                  value={newContact.email}
                  onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                  fullWidth
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Téléphone"
                  value={newContact.telephone}
                  onChange={(e) => setNewContact({ ...newContact, telephone: e.target.value })}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Phone color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Mot de passe"
                  type="password"
                  value={newContact.password}
                  onChange={(e) => setNewContact({ ...newContact, password: e.target.value })}
                  fullWidth
                  helperText="Laissez vide pour un mot de passe par défaut"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={handleCloseAddDialog}
            variant="outlined"
            startIcon={<Cancel />}
            disabled={saving}
          >
            Annuler
          </Button>
          <Button 
            onClick={handleAddContact} 
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <Save />}
            disabled={saving || !newContact.nom || !newContact.prenom || !newContact.email}
            sx={{
              minWidth: 120,
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 4,
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            {saving ? 'Ajout...' : 'Ajouter'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
